import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Megaphone, Pencil, Plus, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api/anuncios/admin';
const AUDIENCIAS = { todos: 'Todos los matriculados', al_dia: 'Solo los que están al día', no_al_dia: 'Solo los que no están al día' };
const vacio = { texto: '', texto_boton: '', link: '', audiencia: 'todos', activo: true };

const AnuncioForm = ({ inicial, onClose, onSaved }) => {
  const [f, setF] = useState(inicial || vacio);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const guardar = async () => {
    setSaving(true);
    try {
      const body = { texto: f.texto, texto_boton: f.texto_boton, link: f.link, audiencia: f.audiencia, activo: f.activo };
      if (inicial?.id) await axios.put(`${API}/${inicial.id}`, body); else await axios.post(API, body);
      toast.success('Anuncio guardado');
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Revisá los datos: el texto debe tener al menos 3 caracteres');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="anuncio-form-dialog">
        <DialogHeader><DialogTitle>{inicial?.id ? 'Editar anuncio' : 'Nuevo anuncio'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Texto</Label><Textarea value={f.texto} onChange={(e) => set('texto', e.target.value)} maxLength={500} data-testid="anuncio-input-texto" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Texto del botón (opcional)</Label><Input value={f.texto_boton} onChange={(e) => set('texto_boton', e.target.value)} maxLength={60} placeholder="Ver más" data-testid="anuncio-input-boton" /></div>
            <div><Label>Link de destino</Label><Input value={f.link} onChange={(e) => set('link', e.target.value)} placeholder="https://… o /beneficios" data-testid="anuncio-input-link" /></div>
          </div>
          <div>
            <Label>¿A quién se muestra?</Label>
            <select className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm" value={f.audiencia}
              onChange={(e) => set('audiencia', e.target.value)} data-testid="anuncio-input-audiencia">
              {Object.entries(AUDIENCIAS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <p className="text-xs text-gray-500 mt-1">"Al día" se toma del último análisis de estado de cuenta del día 10. Quien no tiene análisis solo ve los anuncios para todos.</p>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.activo} onCheckedChange={(v) => set('activo', v)} data-testid="anuncio-input-activo" />Activo</label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving} data-testid="anuncio-guardar-btn">{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Guardar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AnunciosAdmin = () => {
  const [items, setItems] = useState([]);
  const [editando, setEditando] = useState(null);
  const cargar = () => axios.get(API).then((r) => setItems(r.data.items)).catch(() => toast.error('No se pudieron cargar los anuncios'));
  useEffect(() => { cargar(); }, []);

  const toggle = async (a, activo) => { await axios.patch(`${API}/${a.id}`, { activo }); cargar(); };
  const borrar = async (a) => { if (window.confirm('¿Eliminar este anuncio?')) { await axios.delete(`${API}/${a.id}`); cargar(); } };

  return (
    <div className="space-y-4" data-testid="anuncios-admin">
      <Toaster position="top-right" richColors />
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><Megaphone className="h-5 w-5 text-primary" />Anuncios del portal</h2>
          <p className="text-sm text-gray-500">Se muestran arriba de las tarjetas del inicio, a matriculados con sesión iniciada.</p>
        </div>
        <Button onClick={() => setEditando({})} data-testid="anuncio-nuevo-btn"><Plus className="h-4 w-4 mr-1" />Nuevo anuncio</Button>
      </div>
      <div className="space-y-2">
        {items.map((a) => (
          <div key={a.id} className={`flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border p-4 ${a.activo ? 'bg-white' : 'bg-gray-50 opacity-70'}`} data-testid={`anuncio-admin-${a.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-800 break-words">{a.texto}</p>
              <p className="text-xs text-gray-500 mt-1">
                {AUDIENCIAS[a.audiencia]}{a.texto_boton && ` · Botón "${a.texto_boton}" → ${a.link}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={a.activo} onCheckedChange={(v) => toggle(a, v)} data-testid={`anuncio-toggle-${a.id}`} />
              <Button variant="ghost" size="sm" onClick={() => setEditando(a)} data-testid={`anuncio-editar-${a.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="sm" onClick={() => borrar(a)} data-testid={`anuncio-borrar-${a.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
          </div>
        ))}
        {!items.length && <p className="text-sm text-gray-400 py-6 text-center" data-testid="anuncios-vacio">Todavía no hay anuncios.</p>}
      </div>
      {editando && <AnuncioForm inicial={editando.id ? editando : null} onClose={() => setEditando(null)} onSaved={() => { setEditando(null); cargar(); }} />}
    </div>
  );
};
